import { Suspense, useEffect, useLayoutEffect, useMemo } from "react";
import { Routes, Navigate, Route, useLocation, useParams, useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { SkyLoader } from "@skypointcloud/skypoint-ui";
import HeaderSky from "../views/dashboard/header/header";
import SuccessAndErrorMessage from "./successAndErrorMessage/successAndErrorMessage";
import ErrorBoundary from "./shared/ErrorBoundary";
import RouteErrorBoundary from "./shared/RouteErrorBoundary";
import NotificationProvider from "./Notifications/NotificationProvider";
import NoAccessPage from "../views/NoAccessPage";
import { IRouteDetails } from "../common/App.interface";
import { createAppRoutes } from "../routes/appRoutes";
import { useMsalAuth } from "../hooks/useMsalAuth";
import { useDefaultLandingRedirect } from "../hooks/useDefaultLandingRedirect";
import { SidebarProvider, useSidebar } from "../contexts/SidebarContext";
import { useGetMyPermissionsQuery } from "../store/api/rbacApi";
import { useGetPortalBrandingQuery } from "../store/api/customerPortalApi";
import { IRootReducerState } from "../store/IRootReducer";
import { setPortalView } from "../store/slices/uiSlice";
import { ROUTES } from "./shared/constant";
import { useCustomDomain, getCustomDomainUrl } from "../hooks/useCustomDomain";
import { useGetCustomDomainQuery } from "../store/api/customDomainApi";

// Temporarily disabled — waiting on wildcard DNS/TLS infra setup.
// Flip to true to restore the auto-redirect to custom domains for user-only roles.
const CUSTOM_DOMAIN_UI_ENABLED = false;
import SidebarSky from "../views/dashboard/sidebar/sidebar";
import { WorkforceSidebar } from "../views/workforce/nav/WorkforceSidebar";
import { WorkforceRoutes } from "../views/workforce/WorkforceRoutes";
import { useWorkforceGate } from "../views/workforce/hooks/useWorkforceGate";
import { isWorkforceContext, isWorkforcePath } from "../routes/workforcePaths";
import { EmployeeSidebar } from "../views/employee/nav/EmployeeSidebar";
import { LeaveGuardProvider } from "../views/employee/ui/LeaveGuard";
import { EmployeeRoutes } from "../views/employee/EmployeeRoutes";
import { useEmployeeGate } from "../hooks/useEmployeeGate";
import { employeeErrorCode, useGetEmployeeMeQuery } from "../store/api/employeeApi";
import { EMP_ERROR_CODES } from "../../shared/employee/access";
import { employeeShellFor, isEmployeePath } from "../routes/employeePaths";
import { PortalTopBar } from "./navigation/PortalTopBar";
import { PortalWorkTypeSidebar } from "./navigation/PortalWorkTypeSidebar";
import {
  CustomerPortalsPage,
  CustomerPortalDetailPage,
  CustomerMyRequestsPage,
  CustomerRequestDetailPage,
  CustomerKnowledgeBasePage,
  EmployeeWorkspacePage,
  ManagerHubPage,
  MyOrgPage,
} from "../routes/lazyComponents";
import TicketAccessGuard from "./routing/TicketAccessGuard";

interface AppLayoutProps {
  windowWidth: number | null;
  notificationMessage: string;
  hideNewdashboard: boolean;
}

/**
 * Checks if a route should be rendered based on its accessibility conditions
 */
function shouldRenderRoute(route: IRouteDetails): boolean {
  return (
    !route.accessibilityConditions ||
    (route.accessibilityConditions?.length > 0 &&
      route.accessibilityConditions?.some((condition) => condition === true))
  );
}

/**
 * Redirects portal users who land on an agent ticket URL (/portals/:portalKey/items/:itemKey)
 * to the equivalent customer portal URL (/customer/portals/:portalKey/requests/:itemKey).
 * This allows a shared agent URL to work correctly for both admins and end users.
 */
function AgentItemRedirect() {
  const { portalKey, itemKey } = useParams<{ portalKey: string; itemKey: string }>();
  const location = useLocation();
  const target =
    ROUTES.CUSTOMER_REQUEST_DETAIL.replace(":portalKey", portalKey ?? "").replace(
      ":itemKey",
      itemKey ?? "",
    ) + location.search;
  return (
    <Navigate
      to={target}
      replace
    />
  );
}

/**
 * Portal routes for skySupport User role - minimal, Jira-like experience
 */
function PortalRoutes() {
  // Force light mode for all customer-facing portal pages
  useLayoutEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains("dark");
    if (wasDark) root.classList.remove("dark");
    return () => {
      if (wasDark) root.classList.add("dark");
    };
  }, []);

  return (
    <Routes>
      <Route
        path={ROUTES.CUSTOMER_REQUEST_DETAIL + "/:instanceid?"}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading Request Detail">
            <TicketAccessGuard mode="customer">
              <CustomerRequestDetailPage />
            </TicketAccessGuard>
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOMER_MY_REQUESTS + "/:instanceid?"}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading My Requests">
            <CustomerMyRequestsPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOMER_KB_ARTICLE + "/:instanceid?"}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading Article">
            <CustomerKnowledgeBasePage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOMER_KNOWLEDGE_BASE + "/:instanceid?"}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading Knowledge Base">
            <CustomerKnowledgeBasePage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOMER_PORTAL_DETAIL + "/:instanceid?"}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading Portal">
            <CustomerPortalDetailPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOMER_PORTALS + "/:instanceid?"}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading Portals">
            <CustomerPortalsPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.PORTAL_ITEM_DETAIL + "/:instanceid?"}
        element={<AgentItemRedirect />}
      />
      <Route
        path={ROUTES.CUSTOMER_MY_WORKSPACE + "/:instanceid?"}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading My Workspace">
            <EmployeeWorkspacePage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOMER_MY_TEAM + "/:instanceid?"}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading My Team">
            <ManagerHubPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOMER_MY_ORG + "/:instanceid?"}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading My Org">
            <MyOrgPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path="*"
        element={
          <Navigate
            to={ROUTES.CUSTOMER_MY_REQUESTS}
            replace
          />
        }
      />
    </Routes>
  );
}

/**
 * Routes for custom domain mode — same pages as PortalRoutes but without /customer prefix.
 * Rendered when the app detects it's running on a custom subdomain.
 */
function CustomDomainRoutes() {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains("dark");
    if (wasDark) root.classList.remove("dark");
    return () => {
      if (wasDark) root.classList.add("dark");
    };
  }, []);

  return (
    <Routes>
      <Route
        path={ROUTES.CUSTOM_DOMAIN_REQUEST_DETAIL}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading Request Detail">
            <CustomerRequestDetailPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOM_DOMAIN_MY_REQUESTS}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading My Requests">
            <CustomerMyRequestsPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOM_DOMAIN_KB_ARTICLE}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading Article">
            <CustomerKnowledgeBasePage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOM_DOMAIN_KNOWLEDGE_BASE}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading Knowledge Base">
            <CustomerKnowledgeBasePage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOM_DOMAIN_PORTAL_DETAIL}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading Portal">
            <CustomerPortalDetailPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOM_DOMAIN_PORTALS}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading Portals">
            <CustomerPortalsPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOM_DOMAIN_MY_WORKSPACE}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading My Workspace">
            <EmployeeWorkspacePage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOM_DOMAIN_MY_TEAM}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading My Team">
            <ManagerHubPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path={ROUTES.CUSTOM_DOMAIN_MY_ORG}
        element={
          <RouteErrorBoundary fallbackTitle="Error loading My Org">
            <MyOrgPage />
          </RouteErrorBoundary>
        }
      />
      <Route
        path="*"
        element={
          <Navigate
            to={ROUTES.CUSTOM_DOMAIN_MY_REQUESTS}
            replace
          />
        }
      />
    </Routes>
  );
}

/**
 * Main application layout component containing header, sidebar, and content area
 * Pattern: Matches skypoint-skyprivacy's AppLayout architecture
 */
export function AppLayout(props: AppLayoutProps) {
  return (
    <SidebarProvider>
      {/* Lets the employee sidebar ask before leaving a page with unsaved changes. */}
      <LeaveGuardProvider>
        <AppLayoutContent {...props} />
      </LeaveGuardProvider>
    </SidebarProvider>
  );
}

const PORTAL_TOPBAR_PATTERN = /^\/portals\/[^/]+\/(list|board|calendar|queues|reports)/;

function AppLayoutContent({ windowWidth, notificationMessage, hideNewdashboard }: AppLayoutProps) {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const portalView = useSelector((state: IRootReducerState) => state.uiReducer.portalView);
  const { isCustomDomain } = useCustomDomain();
  const workforceGate = useWorkforceGate();
  /**
   * Two different questions, which used to share one answer.
   *
   * `isWorkforceView` decides the CHROME — which sidebar and which product pill
   * — and is true for a module-neutral route opened from Workforce, so the shell
   * does not flip product mid-task (#1046).
   *
   * `rendersWorkforceModule` decides the ROUTE TREE, and must key on the PATH
   * alone. WorkforceRoutes matches only /workforce/* and sends everything else
   * to its People catch-all, so rendering it for a module-neutral route bounced
   * that page straight back to /workforce/people — carrying the marker with it,
   * which made the link look dead. /account has had this since #1046; /skyagent
   * and /skybrain would have inherited it.
   *
   * Module-neutral pages live in the main route tree, so the main tree is what
   * has to render them — while the chrome around them stays Workforce.
   */
  const isWorkforceView = isWorkforceContext(location.pathname, location.search) && !isCustomDomain;
  const rendersWorkforceModule = isWorkforcePath(location.pathname) && !isCustomDomain;
  const employeeGate = useEmployeeGate();
  // skyEmployee's route tree and front door key on the path. The chrome also
  // covers a module-neutral page opened from it (`?from=employee`, e.g. Users
  // & access) — the same split as Workforce above — but only while Employee is
  // on, so a stale link falls back to the Service Desk shell.
  const isEmployeeView = isEmployeePath(location.pathname) && !isCustomDomain;
  const isEmployeeChrome =
    employeeShellFor(location.pathname, location.search, employeeGate.enabled) && !isCustomDomain;

  const {
    data: rbacPermissions,
    isLoading: permissionsLoading,
    isError: permissionsError,
  } = useGetMyPermissionsQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: customDomainData, isLoading: customDomainLoading } = useGetCustomDomainQuery(
    undefined,
    { skip: !CUSTOM_DOMAIN_UI_ENABLED },
  );

  // Use RBAC permissions as the single source of truth for view determination.
  // The my-permissions endpoint already handles B2C admins, custom roles, and legacy roles.
  const isAnyAdmin =
    rbacPermissions?.isAdmin === true ||
    rbacPermissions?.isB2CAdmin === true ||
    rbacPermissions?.isManager === true;
  const isNaturalUserPortal =
    !isAnyAdmin &&
    (rbacPermissions?.isUserPortal === true ||
      (rbacPermissions?.isAdmin === false &&
        rbacPermissions?.isB2CAdmin !== true &&
        !rbacPermissions?.hasNoAppRole));

  // Admin users can switch to user portal view via the header dropdown
  const isAdminViewingAsUser = isAnyAdmin && portalView === "user";

  // skyEmployee's front door: a plain user (no admin or manager role) who is an
  // employee opens into skyEmployee when it is on for the instance, with its
  // header and menu. "An employee" means GET /employee/me answers; anyone else
  // (an external customer of Service Desk, say) keeps the customer portal.
  const checkEmployeeFrontDoor = isNaturalUserPortal && employeeGate.enabled;
  const { data: employeeMe, error: employeeMeError } = useGetEmployeeMeQuery(undefined, {
    skip: !checkEmployeeFrontDoor,
  });
  // Only the server's own "no" (skyEmployee off, or not an employee here) is
  // an answer. A failed refresh (a 5xx or a dropped connection) isn't: the
  // last good answer stands, so nobody is sent away mid-draft.
  const employeeMeCode = employeeErrorCode(employeeMeError);
  const employeeDefinitelyNot =
    employeeMeCode === EMP_ERROR_CODES.NOT_AN_EMPLOYEE ||
    employeeMeCode === EMP_ERROR_CODES.DISABLED;
  const isEmployeeMember = employeeMe != null && !employeeDefinitelyNot;
  const isNotEmployeeMember = employeeDefinitelyNot || (employeeMe == null && !!employeeMeError);
  // Not known yet because /employee/me failed to load at all: on a skyEmployee
  // page, skyEmployee shows its own "couldn't load / Refresh" instead.
  const employeeMeUnknown =
    checkEmployeeFrontDoor && employeeMe == null && !!employeeMeError && !employeeDefinitelyNot;
  const employeeFrontDoor = checkEmployeeFrontDoor && isEmployeeMember;
  // Not decided yet: the product switch, then /employee/me, still loading.
  const employeeFrontDoorPending =
    isNaturalUserPortal &&
    (employeeGate.isLoading ||
      (checkEmployeeFrontDoor && !isEmployeeMember && !isNotEmployeeMember));
  // A plain user following an agent ticket link is sent to its customer view.
  const portalItemMatch = location.pathname.match(/^\/portals\/([^/]+)\/items\/([^/]+)/);
  const portalItemCustomerPath = portalItemMatch
    ? `/customer/portals/${portalItemMatch[1]}/requests/${portalItemMatch[2]}`
    : null;

  // Safety net: when permissions resolve and user is NOT admin/manager,
  // ensure they're on the user portal — prevents getting stuck on admin view
  // after role downgrade or if portalView state is stale.
  // If the instance has a custom domain configured, redirect there instead.
  const isCustomerPortalPath = location.pathname.startsWith("/customer/");

  // Open into the user's preferred product. Blocked while permissions resolve
  // and for users whose navigation the customer-portal redirect below owns —
  // two redirects racing on the same load would fight.
  useDefaultLandingRedirect({
    blocked:
      permissionsLoading ||
      permissionsError ||
      customDomainLoading ||
      isCustomDomain ||
      isNaturalUserPortal,
  });

  useEffect(() => {
    if (permissionsLoading || permissionsError || customDomainLoading) return;
    if (isNaturalUserPortal && portalView !== "user") {
      // Wait until the front door is decided (a quick product and /employee/me
      // check), so this redirect runs once, as before, when it isn't.
      if (employeeFrontDoorPending && !isCustomerPortalPath) return;
      dispatch(setPortalView("user"));
      if (!isCustomerPortalPath && !isCustomDomain) {
        // If instance has a custom domain, redirect to it instead of /customer
        const customDomainUrl = CUSTOM_DOMAIN_UI_ENABLED
          ? getCustomDomainUrl(customDomainData?.prefix ?? null)
          : null;
        if (customDomainUrl) {
          window.location.href = customDomainUrl;
          return;
        }
        const qs = window.location.search;
        if (portalItemCustomerPath) {
          navigate(portalItemCustomerPath + qs, { replace: true });
        } else if (!isEmployeeView && !employeeFrontDoorPending && !employeeFrontDoor) {
          // skyEmployee pages, and an employee opening the app, are handled by
          // the front-door effects below.
          navigate(ROUTES.CUSTOMER_PORTALS + qs, { replace: true });
        }
      }
    }
  }, [
    permissionsLoading,
    permissionsError,
    customDomainLoading,
    isNaturalUserPortal,
    portalView,
    isCustomerPortalPath,
    isCustomDomain,
    customDomainData,
    dispatch,
    navigate,
    portalItemCustomerPath,
    isEmployeeView,
    employeeFrontDoorPending,
    employeeFrontDoor,
  ]);

  // Front door, once decided (P3-1). An employee opening the app (anywhere but
  // the customer portal or a ticket link) lands on skyEmployee Home. A plain
  // user on a skyEmployee page who is not an employee there, or with
  // skyEmployee off, goes to the customer portal as before.
  useEffect(() => {
    if (permissionsLoading || permissionsError || customDomainLoading) return;
    if (!isNaturalUserPortal || employeeFrontDoorPending || isCustomDomain) return;
    const qs = window.location.search;
    if (isEmployeeView) {
      if (!employeeFrontDoor && !employeeMeUnknown) {
        navigate(ROUTES.CUSTOMER_PORTALS + qs, { replace: true });
      }
      return;
    }
    if (employeeFrontDoor && !isCustomerPortalPath && !portalItemCustomerPath) {
      navigate(ROUTES.EMPLOYEE_MY_HOME + qs, { replace: true });
    }
  }, [
    permissionsLoading,
    permissionsError,
    customDomainLoading,
    isNaturalUserPortal,
    employeeFrontDoorPending,
    employeeFrontDoor,
    employeeMeUnknown,
    isEmployeeView,
    isCustomerPortalPath,
    isCustomDomain,
    portalItemCustomerPath,
    navigate,
  ]);

  // When landing on a customer portal path (deep link, back button, etc.),
  // sync portalView to "user" so the dropdown reflects the actual view.
  // Only triggers on URL changes — not on portalView changes — so the
  // dropdown's own admin↔user switch is never overridden mid-navigation.
  useEffect(() => {
    if (isCustomerPortalPath) {
      dispatch(setPortalView("user"));
    }
  }, [isCustomerPortalPath, dispatch]);

  // skyEmployee keeps its own admin/employee view in the URL, not in portalView.
  // An admin or manager arriving while Service Desk's "user" view is on (set
  // from the dropdown or by visiting /customer/*) would otherwise get no header
  // and a misplaced sidebar, so switch back to "admin" there. End users are
  // never touched: their redirect to the customer portal keys off this state.
  useEffect(() => {
    if (isEmployeeView && isAnyAdmin && portalView === "user") {
      dispatch(setPortalView("admin"));
    }
  }, [isEmployeeView, isAnyAdmin, portalView, dispatch]);

  // Customer portal deep links (e.g. from email notifications) should always render
  // PortalRoutes — even for end users who have no agent record (hasNoAppRole) and
  // even for admins who happen to click a customer-facing "View Ticket" link.
  // An employee in skyEmployee (front door) gets the product layout: header,
  // menu and content padding, like admins and managers.
  const isEmployeeFrontDoorView = isEmployeeView && (employeeFrontDoor || employeeMeUnknown);
  const isUserPortal =
    !isEmployeeFrontDoorView &&
    (isCustomDomain || isNaturalUserPortal || isAdminViewingAsUser || isCustomerPortalPath);
  // While the front door is being decided, or just before an employee is sent
  // to skyEmployee Home, hold the customer portal routes: their catch-all would
  // otherwise redirect first.
  const holdForEmployeeFrontDoor =
    isNaturalUserPortal &&
    !isCustomerPortalPath &&
    !isCustomDomain &&
    !isEmployeeView &&
    !portalItemCustomerPath &&
    (employeeFrontDoorPending || employeeFrontDoor);

  // Prefetch branding in parallel with permissions (no skip guard) so portal pages
  // render with cached data. For admin users the result is unused but harmless (~200ms cached).
  useGetPortalBrandingQuery();

  const routes = useMemo(() => createAppRoutes({ hideNewdashboard }), [hideNewdashboard]);

  const filteredRoutes = useMemo(
    () => routes.filter((route) => shouldRenderRoute(route)),
    [routes],
  );

  const hasNoAppRole = rbacPermissions?.hasNoAppRole === true;
  const canAccessSettings = rbacPermissions?.canAccessSettings === true;

  // Don't show the no-access page for customer portal paths — end users visiting a
  // customer portal deep link (e.g. "View Ticket" email link) won't have agent records
  // and would incorrectly land on the no-access page without this guard.
  const shouldShowNoAccessPage = hasNoAppRole && !permissionsLoading && !isCustomerPortalPath;

  const { logout, getQueryString } = useMsalAuth();

  const isFormFillPage = /^\/forms\/[^/]+/.test(location.pathname);
  const hasSidebar = !permissionsLoading && !isUserPortal && !isFormFillPage;
  const isPortalSettings = /^\/portals\/[^/]+\/settings/.test(location.pathname);
  const isInsidePortalView = PORTAL_TOPBAR_PATTERN.test(location.pathname);
  const isItemDetail = /^\/portals\/[^/]+\/items\/[^/]+/.test(location.pathname);
  const isPortalReports = /^\/portals\/[^/]+\/reports/.test(location.pathname);
  const showGlobalSidebar =
    hasSidebar &&
    !isPortalSettings &&
    !isInsidePortalView &&
    !isItemDetail &&
    !isWorkforceView &&
    !isEmployeeChrome;
  const hasPortalTopBar = hasSidebar && isInsidePortalView;

  return (
    <>
      {permissionsLoading ? null : isCustomDomain || isUserPortal || isFormFillPage ? null : (
        <HeaderSky />
      )}
      <ErrorBoundary>
        {/* Flex layout: sidebar takes natural width, content fills remaining space.
            The top padding pushes below the fixed header (and the new-version
            strip above it while it shows). */}
        <div
          className={`flex min-h-screen ${hasSidebar ? "pt-[calc(3.5rem+var(--new-version-h))] md:pt-[calc(52px+var(--new-version-h))]" : ""}`}
        >
          {/* Sidebar column — sticky so it stays pinned while content scrolls.
              On mobile (<992px) SkyNavDrawer switches to overlay mode automatically;
              z-[1000] ensures the overlay renders above the fixed header (z-999). */}
          {showGlobalSidebar && (
            <aside className="sky-nav sticky top-[calc(52px+var(--new-version-h))] h-[calc(100vh-52px-var(--new-version-h))] shrink-0 z-[1000] md:z-810 dark:bg-gray-900">
              <SidebarSky
                collapsed={isCollapsed}
                onCollapseChange={(collapsed) => setIsCollapsed(collapsed)}
              />
            </aside>
          )}
          {isWorkforceView && workforceGate.enabled && (
            <aside className="sky-nav sticky top-[calc(52px+var(--new-version-h))] h-[calc(100vh-52px-var(--new-version-h))] shrink-0 z-[1000] md:z-810 dark:bg-gray-900">
              <WorkforceSidebar
                collapsed={isCollapsed}
                onCollapseChange={(collapsed) => setIsCollapsed(collapsed)}
              />
            </aside>
          )}
          {isEmployeeChrome && (
            <aside className="sky-nav sticky top-[calc(52px+var(--new-version-h))] h-[calc(100vh-52px-var(--new-version-h))] shrink-0 z-[1000] md:z-810 dark:bg-gray-900">
              <EmployeeSidebar
                collapsed={isCollapsed}
                onCollapseChange={(collapsed) => setIsCollapsed(collapsed)}
              />
            </aside>
          )}
          {isInsidePortalView && hasSidebar && !isPortalReports && (
            <aside className="sticky top-[calc(52px+var(--new-version-h))] h-[calc(100vh-52px-var(--new-version-h))] shrink-0 z-[950] hidden md:block dark:bg-gray-900 overflow-visible">
              <PortalWorkTypeSidebar />
            </aside>
          )}

          {/* Content column */}
          <div className="flex-1 min-w-0">
            {/* PortalTopBar is position:fixed — rendered here for DOM order but positions itself */}
            {hasSidebar && <PortalTopBar />}
            <NotificationProvider>
              <div
                className={`content-wrapper ${isUserPortal ? "" : "min-h-[85vh]!"} bg-surface-subtle dark:bg-gray-900`}
              >
                <SuccessAndErrorMessage
                  notificationMessage={notificationMessage}
                  successClassName="successMessageDashboard"
                  failureClassName="errorMessageDashboard"
                  warningClassName="warningMessageDashboard"
                />
                {shouldShowNoAccessPage ? (
                  <NoAccessPage canAccessSettings={canAccessSettings} />
                ) : (
                  <Suspense
                    fallback={
                      <div className="flex justify-center items-center min-h-[400px]">
                        <SkyLoader label="Loading..." />
                      </div>
                    }
                  >
                    {isCustomDomain ? (
                      <CustomDomainRoutes />
                    ) : rendersWorkforceModule ? (
                      workforceGate.isLoading ? null : workforceGate.enabled ? (
                        <WorkforceRoutes />
                      ) : (
                        <Navigate
                          to="/"
                          replace
                        />
                      )
                    ) : isEmployeeView ? (
                      employeeGate.isLoading ? null : employeeGate.enabled ? (
                        <EmployeeRoutes />
                      ) : (
                        <Navigate
                          to="/"
                          replace
                        />
                      )
                    ) : isUserPortal ? (
                      holdForEmployeeFrontDoor ? (
                        <div className="flex justify-center items-center min-h-[400px]">
                          <SkyLoader label="Loading..." />
                        </div>
                      ) : (
                        <PortalRoutes />
                      )
                    ) : (
                      <Routes>
                        {filteredRoutes.map((route) => (
                          <Route
                            key={route.name}
                            path={route.path}
                            element={
                              <RouteErrorBoundary fallbackTitle={`Error loading ${route.name}`}>
                                <route.component
                                  realTimeData={
                                    route.isRealTimeDataOn ? route.realTimeData : undefined
                                  }
                                  resetRealTimeData={
                                    route.isRealTimeDataOn && route.resetRealTimeData
                                      ? route.resetRealTimeData
                                      : undefined
                                  }
                                  windowWidth={windowWidth}
                                  {...route.extraProps}
                                />
                              </RouteErrorBoundary>
                            }
                          />
                        ))}
                        <Route
                          path="*"
                          element={
                            // Don't redirect while permissions are loading — the route may
                            // match PortalRoutes once isUserPortal flips to true.
                            permissionsLoading ? null : (
                              <Navigate
                                to="/"
                                replace
                              />
                            )
                          }
                        />
                      </Routes>
                    )}
                  </Suspense>
                )}
              </div>
            </NotificationProvider>
          </div>
        </div>
      </ErrorBoundary>
    </>
  );
}
